import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import axios from "axios";
// eslint-disable-next-line no-unused-vars
import { FiPlus, FiTrash2, FiUser, FiRefreshCw, FiMoreVertical } from "react-icons/fi";
import { FaEye, FaEdit, FaTrash } from "react-icons/fa";
import { Loader2 } from "lucide-react";

import "./User.css";

const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
  <button
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    type="button"
    className="btn btn-sm btn-light border-0 bg-transparent p-1 shadow-none"
    style={{ outline: "none", cursor: "pointer", display: "inline-flex", alignItems: "center" }}
  >
    {children}
  </button>
));

const User = () => {
  const authState = useSelector((state) => state.auth);
  const loggedInUser = authState?.user ?? authState;
  const token = loggedInUser?.refreshToken || authState?.refreshToken || loggedInUser?.token || authState?.token;
  const userType = loggedInUser?.user?.userType || loggedInUser?.userType;

  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchUsers = async (page = 1) => {
    setIsLoading(true);
    try {
      let response;
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      try {
        response = await axios.post(
          `${baseUrl}/customer/list`,
          {
            page_number: page,
            page_size: 10,
            order_by: "ASC",
          },
          { headers }
        );
      } catch (err) {
        response = await axios.post(
          `${baseUrl}/admin/users`,
          {
            page_number: page,
            page_size: 10,
            order_by: "DSC",
            user_type: "CUSTOMER",
          },
          { headers }
        );
      }

      const usersList =
        response.data?.customerAssets ||
        response.data?.customers ||
        response.data?.users ||
        response.data?.data ||
        response.data?.content ||
        (Array.isArray(response.data) ? response.data : []);

      setData(usersList);
      setTotalPages(response.data?.totalPage || response.data?.total_page || 1);
    } catch (error) {
      console.error("Error fetching customers:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(currentPage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, currentPage]);

  const handleDelete = async (id) => {
    if (!token) return;

    try {
      await axios.delete(`${baseUrl}/customer/deleteCustomer/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    } catch (e) {
      try {
        await axios.delete(`${baseUrl}/admin/deleteUser/${id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
      } catch (err) {
        console.error("Error deleting user:", err);
      }
    }

    setData((prev) => prev.filter((item) => item.id !== id));
  };

  const handleToggle = async (id, enabled) => {
    if (!token) return;

    try {
      await axios.put(
        `${baseUrl}/admin/user/state`,
        { id, active_status: enabled },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      fetchUsers(currentPage);
    } catch (error) {
      console.error("Error toggling user state:", error);
    }
  };

  return (
    <div className="user-page">
      <div className="user-page__inner">
        
        

        <main className="user-page__content">
          
          <div className="user-page__header">
            <div>
              <span className="user-page__eyebrow">User Management</span>
              <h1 className="user-page__title">
                {userType === "PARTNER" ? "Team Management" : "Customers"}
              </h1>
            </div>

            <Link to="/user/add-user" className="user-page__btn">
              <FiPlus size={16} />
              Create User
            </Link>
          </div>

          <div className="user-page__card">
            {isLoading ? (
              <div className="user-page__loader">
                <Loader2 className="user-page__loader-icon" size={32} />
                <span>Loading users…</span>
              </div>
            ) : data.length === 0 ? (
              <div className="user-page__empty">
                <FiUser size={28} />
                <p>No users found</p>
                <Link to="/user/add-user" className="user-page__ghost-btn">
                  Create First User
                </Link>
              </div>
            ) : (
              <>
                <table className="user-page__table">
                  <thead>
                    <tr>
                      <th>S.No.</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((item, index) => {
                      const isProtectedAdmin =
                        item.roles?.includes("SUPER_ADMIN") ||
                        item.roles?.includes("ADMIN") ||
                        item.user_type === "SUPER_ADMIN" ||
                        item.user_type === "ADMIN" ||
                        item.id === loggedInUser?.user?.id ||
                        item.id === loggedInUser?.id;

                      return (
                        <tr key={item.id || index}>
                          <td>{(currentPage - 1) * 10 + index + 1}</td>
                          <td>
                            <Link to={`/user/${item.id}`} style={{ textDecoration: "none", color: "inherit", fontWeight: "500" }}>
                              {[item.first_name, item.last_name].filter(Boolean).join(" ") || item.name || item.customer_name || "Customer"}
                            </Link>
                          </td>
                          <td>{item.email_address || item.email_id || item.mobile_number || "-"}</td>
                          <td>{item.roles?.[0] || item.user_type || "CUSTOMER"}</td>
                          <td>
                            <button
                              className={`user-page__status ${
                                item.active || item.active_status ? "active" : ""
                              }`}
                              style={{ opacity: isProtectedAdmin ? 0.7 : 1, cursor: isProtectedAdmin ? "not-allowed" : "pointer" }}
                              disabled={isProtectedAdmin}
                              title={isProtectedAdmin ? "Admin accounts cannot be disabled" : "Toggle status"}
                              onClick={() =>
                                !isProtectedAdmin && handleToggle(item.id, !(item.active || item.active_status))
                              }
                            >
                              {item.active || item.active_status ? "Active" : "Disabled"}
                            </button>
                          </td>
                          <td>
                            <Dropdown align="end" drop="auto" popperConfig={{ strategy: "fixed" }}>
                              <Dropdown.Toggle as={CustomToggle} id={`dropdown-user-${item.id}`}>
                                <FiMoreVertical size={18} />
                              </Dropdown.Toggle>
                              <Dropdown.Menu className="shadow-sm border">
                                <Dropdown.Item as={Link} to={`/user/${item.id}`}>
                                  <FaEye className="me-2 text-primary" /> View Details
                                </Dropdown.Item>
                                <Dropdown.Item as={Link} to={`/user/${item.id}/edit`}>
                                  <FaEdit className="me-2 text-warning" /> Edit User
                                </Dropdown.Item>
                                {!isProtectedAdmin && (
                                  <>
                                    <Dropdown.Divider />
                                    <Dropdown.Item
                                      onClick={() => handleDelete(item.id)}
                                      className="text-danger"
                                    >
                                      <FaTrash className="me-2 text-danger" /> Delete User
                                    </Dropdown.Item>
                                  </>
                                )}
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="user-page__pagination">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                  >
                    Prev
                  </button>

                  <span>
                    {currentPage} / {totalPages}
                  </span>

                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default User;